import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { useAuth } from '../context/AuthContext';
import { STATIC_PLANS } from '../data/staticPlans';
import { useBillingPage } from '../hooks/useBillingPage';
import { usePricingData } from '../hooks/usePricingData';
import { errorMessage } from '../services/apiError';
import { getDetectedCountry } from '../services/countryService';
import { restoreApplePurchasesForCurrentAccount } from '../services/paymentActions';
import type { PlanConfig } from '../services/subscriptionService';
import { MINIMUM_TOP_UP_CREDITS } from '../utils/creditGate';
import { isCreditAllowanceHighlight } from '../utils/planCreditSummary';
import { isIOSApp } from '../utils/platformDetection';
import { getDefaultPricingCurrency } from '../utils/pricingCountry';
import { getPlanBillingInterval } from '../utils/pricingPlans';
import { isIndiaCountry } from '../utils/region';

import { CreditTopUpCard } from './billing/CreditTopUpCard';
import { BillingCurrencySelect } from './billing/CreditTopUpComponents';
import { PlanCreditBreakdown } from './PlanCreditBreakdown';
import { useToast } from './Toast/Toast';

import './ChatBox/UpgradeModal.css';

interface UpgradeModalProps {
  onlyHigherPlans?: boolean;
  onClose: () => void;
  subtitle?: string;
}

interface BillingModalProps extends UpgradeModalProps {
  initialView?: 'plans' | 'credits';
  initialCreditAmount?: number;
}

const UPGRADE_PLANS = [
  {
    key: 'free',
    name: 'Trial',
    price: 'Free',
    period: '',
    description: 'Get started building with AI agents. No credit card required.',
    badge: null as string | null,
    featured: false,
    isCurrent: true,
    highlights: [
      '10 Credits on sign-up',
      'Top-up: $0.34 / credit',
      '1 User',
      'All AI Agents included',
    ],
  },
  {
    key: 'standard',
    name: 'Standard',
    price: '$4.49',
    period: '/month',
    description: 'Affordable entry point with all AI agents included.',
    badge: 'Most Popular',
    featured: true,
    isCurrent: false,
    highlights: [
      '50 Credits / month',
      'Top-up: $0.34 / credit',
      '+1.5 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
    ],
  },
  {
    key: 'standard_india',
    name: 'Standard',
    price: '$1.99',
    period: '/month',
    description: 'Affordable India entry point with all AI agents included.',
    badge: 'Most Popular',
    featured: true,
    isCurrent: false,
    highlights: [
      '20 Credits / month',
      'Top-up: $0.34 / credit',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
    ],
  },
  {
    key: 'plus',
    name: 'Plus',
    price: '$25',
    period: '/month',
    description: 'For growing teams that need more power and unlimited users.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      '350 Credits / month (79% discount vs base)',
      'Top-up: $0.24 / credit (29% discount vs base)',
      '+10.5 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$250',
    period: '/month',
    description: 'Maximum performance for professional teams shipping to production.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      '2,500 Credits / month (70.5% discount vs base)',
      'Top-up: $0.19 / credit (44% discount vs base)',
      '+75 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
      'Custom Agent',
    ],
  },
  {
    key: 'unlimited',
    name: 'Unlimited',
    price: '$1,499',
    period: '/month',
    description: 'Unlimited included usage with no plan-credit wallet deductions.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      'Unlimited Credits included',
      'Usage recorded without wallet deductions',
      'No credit top-ups required',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
      'Custom Agent',
    ],
  },
];

type UpgradePlan = typeof UPGRADE_PLANS[number];
type BillingToast = { success: (msg: string) => void; error: (msg: string) => void };
type BillingCycle = 'monthly' | 'annual';
type BillingCurrency = 'USD' | 'INR';
const PLAN_RANK: Record<string, number> = {
  free: 0,
  trial: 0,
  standard: 1,
  standard_india: 1,
  byok: 1,
  plus: 2,
  pro: 3,
  unlimited: 4,
};

const BILLING_TOPUP_SUCCESS_EVENT = 'billing_topup_success';
const BILLING_UPGRADE_SUCCESS_EVENT = 'billing_upgrade_success';
const formatPlanMoney = (amount: number, currency: 'USD' | 'INR'): string => {
  const hasCents = !Number.isInteger(Number(amount.toFixed(2)));
  const formatted = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount);

  return `${currency === 'INR' ? '₹' : '$'}${formatted}`;
};
const formatAppleMoney = (amount: number): string => `$${amount.toFixed(2)}`;

const getPlanCycleAmounts = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
) => {
  const plan = pricingPlans[planKey] || STATIC_PLANS[planKey] || STATIC_PLANS.free;

  if (isIOSApp()) {
    return {
      monthly: plan.apple_price_monthly ?? plan.monthly_price,
      annual: plan.apple_price_annual ?? plan.annual_price ?? 0,
    };
  }

  if (currency === 'INR') {
    return {
      monthly: plan.monthly_price_inr ?? (plan.monthly_price * 80),
      annual: plan.annual_price_inr ?? ((plan.annual_price ?? 0) * 80),
    };
  }

  return { monthly: plan.monthly_price, annual: plan.annual_price ?? 0 };
};

const getPlanDisplayPrice = (
  planKey: string,
  currency: 'USD' | 'INR',
  billingCycle: BillingCycle = 'monthly',
  pricingPlans: Record<string, PlanConfig> = STATIC_PLANS,
): string => {
  if (planKey === 'free') return 'Free';

  const { monthly, annual } = getPlanCycleAmounts(planKey, currency, pricingPlans);
  const interval = getPlanBillingInterval(planKey, billingCycle === 'annual');
  const amount = interval === 'annual' ? annual / 12 : monthly;

  return isIOSApp() ? formatAppleMoney(amount) : formatPlanMoney(amount, currency);
};

const getPlanTopupRateLabel = (planKey: string, currency: 'USD' | 'INR'): string => {
  const plan = STATIC_PLANS[planKey] || STATIC_PLANS.free;
  if (plan.topups_allowed === false) return 'Not available';

  if (isIOSApp()) {
    return `${formatAppleMoney(plan.topup_price_per_credit_apple ?? plan.topup_price_per_credit ?? 0)} / credit`;
  }

  const amount = currency === 'INR'
    ? (plan.topup_price_per_credit_inr ?? plan.topup_price_per_credit)
    : plan.topup_price_per_credit;

  return `${formatPlanMoney(amount ?? 0, currency)} / credit`;
};

const getPlanHighlights = (plan: UpgradePlan, currency: 'USD' | 'INR'): string[] => {
  if (plan.key === 'free' || plan.key === 'unlimited') {
    return plan.highlights.filter((text) => !isCreditAllowanceHighlight(text));
  }

  const [firstHighlight, , ...rest] = plan.highlights;

  return [
    firstHighlight,
    `Top-up: ${getPlanTopupRateLabel(plan.key, currency)}`,
    ...rest,
  ].filter((text) => !isCreditAllowanceHighlight(text));
};

const getPlanRank = (tier: string): number => PLAN_RANK[tier.trim().toLowerCase()] ?? PLAN_RANK.free;

const getRegionalUpgradePlans = (userTier: string, userCountry?: string): UpgradePlan[] => {
  const currentStandardKey = userTier === 'standard' || userTier === 'standard_india' ? userTier : null;
  const standardKey = currentStandardKey || (isIndiaCountry(userCountry) && !isIOSApp() ? 'standard_india' : 'standard');

  return UPGRADE_PLANS.filter((plan) => (
    (plan.key !== 'standard' && plan.key !== 'standard_india') || plan.key === standardKey
  ));
};

const getVisibleUpgradePlans = (
  userTier: string,
  onlyHigherPlans: boolean,
  userCountry?: string,
): UpgradePlan[] => {
  const regionalPlans = getRegionalUpgradePlans(userTier, userCountry);

  if (!onlyHigherPlans) return regionalPlans;

  const currentRank = getPlanRank(userTier);

  return regionalPlans.filter((plan) => getPlanRank(plan.key) > currentRank);
};

const PlanCheck: React.FC = () => (
  <svg className="upgrade-plan-check" viewBox="0 0 16 16" fill="none">
    <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlanCardCta: React.FC<{
  plan: UpgradePlan; userTier: string; isLoading?: boolean; onSelect: () => void;
}> = ({ plan, userTier, isLoading, onSelect }) => {
  if (plan.key === userTier) {
    return <button className="upgrade-plan-cta upgrade-plan-cta--current" disabled>Current Plan</button>;
  }

  if (plan.key === 'free') return null;

  return (
    <button
      className={`upgrade-plan-cta${plan.featured ? ' upgrade-plan-cta--featured' : ''}${isLoading ? ' upgrade-plan-cta--loading' : ''}`}
      onClick={onSelect}
      disabled={isLoading}
      type="button"
    >
      {isLoading ? 'Processing...' : 'Upgrade'}
    </button>
  );
};

const MOBILE_TRUNCATE = 2;

const PlanCard: React.FC<{
  plan: UpgradePlan;
  index: number;
  onSelect: () => void;
  isLoading?: boolean;
  userTier: string;
  currency: 'USD' | 'INR';
  billingCycle: BillingCycle;
  planConfig: PlanConfig;
}> = ({ plan, index, onSelect, isLoading, userTier, currency, billingCycle, planConfig }) => {
  const isCurrent = plan.key === userTier;
  const [expanded, setExpanded] = useState(false);
  const planHighlights = getPlanHighlights(plan, currency);
  const canTruncate = planHighlights.length > MOBILE_TRUNCATE;
  const visibleHighlights = expanded ? planHighlights : planHighlights.slice(0, MOBILE_TRUNCATE);
  const pricingPlans = { [plan.key]: planConfig };
  const annualTotal = getPlanCycleAmounts(plan.key, currency, pricingPlans).annual;
  const billedAnnually = getPlanBillingInterval(plan.key, billingCycle === 'annual') === 'annual';
  const period = billedAnnually && plan.key !== 'free'
    ? `${isIOSApp() ? formatAppleMoney(annualTotal) : formatPlanMoney(annualTotal, currency)} billed yearly`
    : (plan.period || 'Forever free');

  return (
    <div
      className={`upgrade-plan-card${plan.featured ? ' upgrade-plan-card--featured' : ''}${isCurrent ? ' upgrade-plan-card--current' : ''}`}
      style={{ animationDelay: `${0.08 + index * 0.08}s` }}
    >
      {plan.featured && <div className="upgrade-plan-glow" />}
      {isCurrent && <span className="upgrade-plan-badge upgrade-plan-badge--current">Current Plan</span>}
      {!isCurrent && plan.badge && <span className="upgrade-plan-badge">{plan.badge}</span>}
      <div className="upgrade-plan-info">
        <div className="upgrade-plan-name">{plan.name}</div>
        <div className="upgrade-plan-price">
          <span className="upgrade-plan-amount">{getPlanDisplayPrice(plan.key, currency, billingCycle, pricingPlans)}</span>
          <span className="upgrade-plan-period">
            {billedAnnually && plan.key !== 'free' ? '/month' : period}
          </span>
        </div>
        {billedAnnually && plan.key !== 'free' && (
          <p className="upgrade-plan-billing-detail">{period}</p>
        )}
        {plan.key === 'unlimited' && <p className="upgrade-plan-billing-detail">Billed monthly only</p>}
        <p className="upgrade-plan-desc">{plan.description}</p>
        <PlanCreditBreakdown planKey={plan.key} plan={planConfig} isAnnual={billedAnnually} />
        <ul className="upgrade-plan-highlights upgrade-plan-highlights--full">
          {planHighlights.map((h) => (<li key={h} className={h.includes('discount') ? 'upgrade-plan-highlight--discount' : ''}><PlanCheck />{h}</li>))}
        </ul>
        <ul className="upgrade-plan-highlights upgrade-plan-highlights--mobile">
          {visibleHighlights.map((h) => (<li key={h} className={h.includes('discount') ? 'upgrade-plan-highlight--discount' : ''}><PlanCheck />{h}</li>))}
        </ul>
        {canTruncate && (
          <button
            className="upgrade-plan-seemore"
            onClick={() => setExpanded((p) => !p)}
            type="button"
          >
            {expanded ? 'See less ▴' : `See more (${planHighlights.length - MOBILE_TRUNCATE}) ▾`}
          </button>
        )}
      </div>
      <PlanCardCta plan={plan} userTier={userTier} isLoading={isLoading} onSelect={onSelect} />
    </div>
  );
};

const getPlanTopupRate = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): number => {
  const plan = pricingPlans[planKey] || STATIC_PLANS[planKey] || STATIC_PLANS.free;

  if (plan.topups_allowed === false) return 0;
  if (isIOSApp()) return plan.topup_price_per_credit_apple ?? plan.topup_price_per_credit ?? 0;

  return currency === 'INR'
    ? (plan.topup_price_per_credit_inr ?? plan.topup_price_per_credit ?? 0)
    : (plan.topup_price_per_credit ?? 0);
};

const getAnnualDiscountPercent = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): number => {
  const { monthly, annual } = getPlanCycleAmounts(planKey, currency, pricingPlans);

  if (monthly <= 0 || annual <= 0) return 0;

  return Math.max(0, Math.round((1 - (annual / (monthly * 12))) * 100));
};

type DiscountedUpgradePlan = {
  plan: UpgradePlan;
  savingsPercent: number;
};

const getDiscountedUpgradePlans = (
  plans: UpgradePlan[],
  userTier: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): DiscountedUpgradePlan[] => {
  const currentRate = getPlanTopupRate(userTier, currency, pricingPlans);

  if (currentRate <= 0) return [];

  return plans
    .filter((plan) => getPlanTopupRate(plan.key, currency, pricingPlans) > 0)
    .map((plan) => ({
      plan,
      savingsPercent: Math.round((1 - (getPlanTopupRate(plan.key, currency, pricingPlans) / currentRate)) * 100),
    }))
    .filter(({ savingsPercent }) => savingsPercent > 0)
    .slice(0, 2);
};

const getAdvertisedPlanSavings = (plan: UpgradePlan): number => {
  const savingsMatch = plan.highlights
    .map((highlight) => highlight.match(/([0-9]+(?:\.[0-9]+)?)% (?:discount|savings)/i))
    .find(Boolean);

  return savingsMatch ? Number(savingsMatch[1]) : 0;
};

const SubscriptionSavingsOffer: React.FC<{
  plans: DiscountedUpgradePlan[];
  onViewPlans: () => void;
}> = ({ plans, onViewPlans }) => {
  if (plans.length === 0) return null;

  const totalSavings = Math.max(...plans.map(({ plan, savingsPercent }) => (
    Math.max(getAdvertisedPlanSavings(plan), savingsPercent)
  )));

  return (
    <section className="billing-subscribe-offer" aria-labelledby="billing-subscribe-offer-title">
      <div className="billing-subscribe-offer__copy">
        <span className="billing-subscribe-offer__eyebrow">
          Up to {totalSavings}% total savings
        </span>
        <div>
          <h4 id="billing-subscribe-offer-title">Get monthly credits and cheaper top-ups</h4>
          <p>Includes lower top-up rates and annual billing savings.</p>
        </div>
      </div>
      <button className="billing-subscribe-offer__compare" onClick={onViewPlans} type="button">
        View plans <span aria-hidden="true">→</span>
      </button>
    </section>
  );
};

const loadUpgradeModules = () => import('../services/paymentActions');

const dispatchBillingSuccessEvent = (eventName: string) => {
  window.dispatchEvent(new CustomEvent(eventName));
};

const handlePaymentSuccess = async (
  refreshSubscription: (cb: () => void) => Promise<void>,
  refreshUser: () => Promise<void>,
  toast: { success: (msg: string) => void; error: (msg: string) => void }, onClose: () => void, msg: string,
) => {
  await refreshSubscription(() => { });
  await refreshUser();
  dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
  toast.success(msg);
  onClose();
};

const executeUpgrade = async (
  tier: string,
  isAnnual: boolean,
  currency: BillingCurrency,
  user: { email?: string; country?: string; id?: string; subscription_tier?: string } | null,
  refreshUser: () => Promise<void>,
  toast: { success: (msg: string) => void; error: (msg: string) => void }, onClose: () => void,
) => {
  if (isIOSApp()) {
    const [{ STATIC_PLANS }, { initiateAppleSubscription, refreshSubscription }] = await Promise.all([
      import('../data/staticPlans'),
      import('../services/paymentActions'),
    ]);
    const plan = STATIC_PLANS[tier];
    const productId = isAnnual ? plan?.apple_product_id_annual : plan?.apple_product_id_monthly;

    if (!productId) {
      toast.error('Apple subscription product not configured for this plan.');

      return;
    }

    await initiateAppleSubscription(
      productId,
      async (upgradedTier: string) => {
        try {
          await refreshSubscription(() => { });
          await refreshUser();
          dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
          toast.success(`Successfully upgraded to ${plan?.name || upgradedTier}!`);
          onClose();
        } catch {
          toast.success('Your upgrade was successful. Please refresh the page to see your updated plan.');
          onClose();
        }
      },
      (error: string) => toast.error(error),
      user?.id,
    );

    return;
  }

  const { initiateUpgrade, refreshSubscription, verifyTransaction } = await loadUpgradeModules();

  await initiateUpgrade(tier, isAnnual, user?.email, async (response, msg) => {
    try {
      await verifyTransaction(response);
      await handlePaymentSuccess(
        refreshSubscription, refreshUser, toast, onClose, msg,
      );
    } catch {
      toast.error('Your upgrade was successful. Please refresh the page to see your updated plan.');
    }
  }, currency);
};

const applyRestoreResult = async (
  result: Awaited<ReturnType<typeof restoreApplePurchasesForCurrentAccount>>,
  refreshUser: () => Promise<void>,
  toast: BillingToast,
  onClose: () => void,
) => {
  if (result.status === 'none') {
    toast.error('No active Apple subscription was found to restore for this Apple ID.');

    return;
  }

  await refreshUser();
  dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
  toast.success(`Restored ${result.tier ? `${result.tier} plan` : 'your Apple subscription'}.`);
  onClose();
};

const executeRestorePurchases = async (
  refreshUser: () => Promise<void>,
  toast: BillingToast,
  onClose: () => void,
) => {
  const result = await restoreApplePurchasesForCurrentAccount();

  await applyRestoreResult(result, refreshUser, toast, onClose);
};

const useLazyUpgrade = (onClose: () => void, currency: BillingCurrency) => {
  const [upgradingTier, setUpgradingTier] = useState<string | null>(null);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const { user, getCurrentUser } = useAuth();
  const toast = useToast();

  const handleUpgrade = useCallback(async (tier: string, isAnnual = false) => {
    setUpgradingTier(tier);

    try {
      await executeUpgrade(tier, isAnnual, currency, user, getCurrentUser, toast, onClose);
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'We couldn’t start the upgrade. Check the selected plan and try again.'));
    } finally {
      setUpgradingTier(null);
    }
  }, [currency, getCurrentUser, user, toast, onClose]);

  const handleRestorePurchases = useCallback(async () => {
    if (!isIOSApp()) return;

    setRestoringPurchases(true);
    try {
      await executeRestorePurchases(getCurrentUser, toast, onClose);
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'We couldn’t restore the Apple purchase. Confirm the Apple ID and try again.'));
    } finally {
      setRestoringPurchases(false);
    }
  }, [getCurrentUser, toast, onClose]);

  return { upgradingTier, restoringPurchases, handleUpgrade, handleRestorePurchases };
};

const getModalFocusTargets = (modal: HTMLDivElement): HTMLElement[] => (
  Array.from(modal.querySelectorAll<HTMLElement>([
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(','))).filter((element) => element.getAttribute('aria-hidden') !== 'true')
);

const useBillingModalKeyboard = (
  modalRef: React.RefObject<HTMLDivElement | null>,
  onClose: () => void,
) => {
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => {
      const modal = modalRef.current;
      const preferredTarget = modal?.querySelector<HTMLElement>('.credit-input')
        || modal?.querySelector<HTMLElement>('.upgrade-modal-close');

      preferredTarget?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();

        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const targets = getModalFocusTargets(modalRef.current);

      if (targets.length === 0) return;

      const firstTarget = targets[0];
      const lastTarget = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === firstTarget) {
        event.preventDefault();
        lastTarget.focus();
      } else if (!event.shiftKey && document.activeElement === lastTarget) {
        event.preventDefault();
        firstTarget.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [modalRef, onClose]);
};

const BillingModalInner = ({
  onlyHigherPlans = true,
  onClose,
  subtitle,
  initialView = 'plans',
  initialCreditAmount,
}: BillingModalProps): React.ReactElement => {
  const { user } = useAuth();
  const userTier = (user?.subscription_tier || 'free').toLowerCase();
  const subscriptionStatus = (user?.subscription_status || 'active').toLowerCase();
  const isUnlimited = userTier === 'unlimited' && ['active', 'cancel_pending'].includes(subscriptionStatus);
  const billingPageState = useBillingPage();
  const { plans: pricingPlans } = usePricingData();
  const modalRef = useRef<HTMLDivElement>(null);
  const preselectedCredits = Math.max(
    MINIMUM_TOP_UP_CREDITS,
    Math.ceil(initialCreditAmount ?? 0),
  );
  const [activeView, setActiveView] = useState<'plans' | 'credits'>(
    isUnlimited && initialView === 'credits' ? 'plans' : initialView,
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [currencyOverride, setCurrencyOverride] = useState<BillingCurrency | null>(null);
  const billingCountry = user?.country || detectedCountry;
  const displayCurrency = currencyOverride ?? getDefaultPricingCurrency(billingCountry, isIOSApp());
  const {
    upgradingTier,
    restoringPurchases,
    handleUpgrade,
    handleRestorePurchases,
  } = useLazyUpgrade(onClose, displayCurrency);

  useBillingModalKeyboard(modalRef, onClose);

  useEffect(() => {
    setActiveView(isUnlimited && initialView === 'credits' ? 'plans' : initialView);
  }, [initialView, isUnlimited]);

  useEffect(() => {
    if (user?.country) {
      setDetectedCountry(null);

      return;
    }

    let cancelled = false;

    getDetectedCountry()
      .then((country) => {
        if (!cancelled) setDetectedCountry(country);
      })
      .catch(() => {
        if (!cancelled) setDetectedCountry(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.country]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const closeOnTopUpSuccess = () => onClose();

    window.addEventListener(BILLING_TOPUP_SUCCESS_EVENT, closeOnTopUpSuccess);

    return () => window.removeEventListener(BILLING_TOPUP_SUCCESS_EVENT, closeOnTopUpSuccess);
  }, [onClose]);

  const visiblePlans = useMemo(() => {
    const plans = getVisibleUpgradePlans(userTier, onlyHigherPlans, billingCountry || undefined);

    return isIOSApp()
      ? plans.filter((plan) => plan.key !== 'pro' && plan.key !== 'unlimited')
      : plans;
  }, [billingCountry, onlyHigherPlans, userTier]);
  const annualDiscount = useMemo(() => Math.max(0, ...visiblePlans.map((plan) => (
    getAnnualDiscountPercent(plan.key, displayCurrency, STATIC_PLANS)
  ))), [displayCurrency, visiblePlans]);
  const cyclePlans = billingCycle === 'annual'
    ? visiblePlans.filter((plan) => getPlanBillingInterval(plan.key, true) === 'annual')
    : visiblePlans;

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (isUnlimited) return;

    event.preventDefault();
    const nextView = activeView === 'credits' ? 'plans' : 'credits';

    setActiveView(nextView);
    window.requestAnimationFrame(() => document.getElementById(`billing-${nextView}-tab`)?.focus());
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const stopModalEventPropagation = (event: React.SyntheticEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return ReactDOM.createPortal(
    <div className="upgrade-modal-overlay" onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={`upgrade-modal upgrade-modal--${activeView}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        onPointerDown={stopModalEventPropagation}
        onMouseDown={stopModalEventPropagation}
        onTouchStart={stopModalEventPropagation}
        onClick={stopModalEventPropagation}
      >
        <button className="upgrade-modal-close" onClick={onClose} type="button" aria-label="Close billing options">✕</button>
        <div className="upgrade-modal-header">
          <span className="upgrade-modal-eyebrow">Billing</span>
          <h3 id="upgrade-modal-title">
            {/* Must match the amount the form actually preselects, or the
                heading promises a purchase the field does not offer. */}
            {activeView === 'credits' && initialCreditAmount
              ? `Add ${preselectedCredits} credits to continue`
              : (activeView === 'credits' ? 'Add credits and keep building' : 'Choose a plan')}
          </h3>
          <p>
            {activeView === 'credits'
              ? (initialCreditAmount
                ? 'Enough for the selected work. Your project stays saved while you top up.'
                : 'Choose how many credits to add. Your work stays exactly where it stopped.')
              : (isUnlimited
                ? 'Your retained wallet stays unchanged while Unlimited usage is active.'
                : 'Get monthly credits and a lower rate for additional top-ups.')}
          </p>
          {subtitle && <p className="upgrade-modal-subtitle">{subtitle}</p>}
        </div>

        <div className="upgrade-modal-tabs" role="tablist" aria-label="Billing options">
          {!isUnlimited && (
            <button
              id="billing-credits-tab"
              className={`upgrade-modal-tab${activeView === 'credits' ? ' upgrade-modal-tab--active' : ''}`}
              onClick={() => setActiveView('credits')}
              onKeyDown={handleTabKeyDown}
              role="tab"
              aria-selected={activeView === 'credits'}
              aria-controls="billing-credits-panel"
              type="button"
            >
              Add Credits
            </button>
          )}
          <button
            id="billing-plans-tab"
            className={`upgrade-modal-tab${activeView === 'plans' ? ' upgrade-modal-tab--active' : ''}`}
            onClick={() => setActiveView('plans')}
            onKeyDown={handleTabKeyDown}
            role="tab"
            aria-selected={activeView === 'plans'}
            aria-controls="billing-plans-panel"
            type="button"
          >
            Plans &amp; savings
          </button>
        </div>

        {activeView === 'plans' ? (
          <div id="billing-plans-panel" role="tabpanel" aria-labelledby="billing-plans-tab">
            <div className="billing-plan-controls">
              {!isIOSApp() && (
                <label className="billing-currency-selector" htmlFor="billing-plan-currency">
                  <span>Currency</span>
                  <BillingCurrencySelect
                    id="billing-plan-currency"
                    currency={displayCurrency}
                    setCurrency={(nextCurrency) => setCurrencyOverride(nextCurrency as BillingCurrency)}
                  />
                </label>
              )}
              <div className="billing-cycle-selector">
                <span>Billing cycle</span>
                <div role="group" aria-label="Subscription billing cycle">
                  <button
                    className={billingCycle === 'monthly' ? 'active' : ''}
                    onClick={() => setBillingCycle('monthly')}
                    aria-pressed={billingCycle === 'monthly'}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={billingCycle === 'annual' ? 'active' : ''}
                    onClick={() => setBillingCycle('annual')}
                    aria-pressed={billingCycle === 'annual'}
                    type="button"
                  >
                    Annual {annualDiscount > 0 && <span>Save {annualDiscount}%</span>}
                  </button>
                </div>
              </div>
            </div>
            <div className={`upgrade-plans-grid upgrade-plans-grid--count-${cyclePlans.length}`}>
              {cyclePlans.map((plan, i) => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  index={i}
                  onSelect={() => handleUpgrade(plan.key, billingCycle === 'annual')}
                  isLoading={upgradingTier === plan.key}
                  userTier={userTier}
                  currency={displayCurrency}
                  billingCycle={billingCycle}
                  planConfig={{ ...STATIC_PLANS[plan.key], ...pricingPlans[plan.key] }}
                />
              ))}
            </div>
            {cyclePlans.length === 0 && (
              <div className="upgrade-modal-empty">
                {visiblePlans.length > 0
                  ? 'No annual upgrades are available. Select Monthly to see available plans.'
                  : 'You are already on the highest available plan.'}
              </div>
            )}
            {isIOSApp() && (
              <div className="upgrade-restore-panel">
                <button
                  className="upgrade-restore-button"
                  onClick={handleRestorePurchases}
                  disabled={restoringPurchases}
                  aria-busy={restoringPurchases}
                  type="button"
                >
                  {restoringPurchases && <span className="restore-button-spinner" aria-hidden="true" />}
                  <span>{restoringPurchases ? 'Restoring...' : 'Restore Plan Purchase'}</span>
                </button>
                <p>
                  Restores active Apple plan subscriptions only. Credit top-ups are consumable and are not restored.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div
            className="upgrade-modal-credit-panel"
            id="billing-credits-panel"
            role="tabpanel"
            aria-labelledby="billing-credits-tab"
          >
            <CreditTopUpCard
              onTopUp={billingPageState.handleTopUp}
              isProcessing={billingPageState.isProcessing}
              onAppleTopUp={billingPageState.onAppleTopUp}
              initialCreditAmount={initialCreditAmount}
              fallbackPlanId={userTier}
              currency={displayCurrency}
              onCurrencyChange={setCurrencyOverride}
              secondaryOffer={({ plans, currentPlanId, currency }) => {
                const discountedPlans = getDiscountedUpgradePlans(
                  visiblePlans,
                  currentPlanId || userTier,
                  currency,
                  plans,
                );

                return (
                  <SubscriptionSavingsOffer
                    plans={discountedPlans}
                    onViewPlans={() => {
                      setBillingCycle('annual');
                      setActiveView('plans');
                    }}
                  />
                );
              }}
            />
          </div>
        )}

      </div>
    </div>,
    document.body,
  );
};

export function BillingModal(props: BillingModalProps): React.ReactElement {
  return <BillingModalInner {...props} />;
}

export function UpgradeModal(props: UpgradeModalProps): React.ReactElement {
  return <BillingModalInner {...props} initialView="plans" />;
}

export { BILLING_TOPUP_SUCCESS_EVENT, BILLING_UPGRADE_SUCCESS_EVENT };
